import React, { useCallback } from "react";

import {
  Axis,
  Chart,
  Legend,
  LineAdvance,
  Slider,
  Tooltip,
} from "bizcharts";

import useTranslations from "../../../hooks/useTranslations";
import useDateFnsLocale from "../../../hooks/useDateFnsLocale";

import { getClosestGrade } from "../../../utils/reviewUtils";
import { dateFormatter } from "../../../hooks/useFormatDate";

type UserGradeOverTimeLineChart = {
  data: {
    user: string;
    reportId: string;
    date: Date;
    gradeType: string;
    grade: number;
  }[];
  options: { value: number; label: string }[];
};

const UserGradeOverTimeLineChart: React.FC<UserGradeOverTimeLineChart> = ({
  data,
  options,
}) => {
  const t = useTranslations();
  const dateFnsLocale = useDateFnsLocale();

  const formatter = useCallback(
    (v: string | number | Date) =>
      dateFormatter(
        // Bizcharts internally converts Date to timestamp string
        typeof v === "string" ? parseInt(v) : v,
        t(`dateFormat.short`) as string,
        t("dateFormat.fallback") as string,
        dateFnsLocale
      ),
    [dateFnsLocale]
  );

  return (
    <Chart
      autoFit
      data={data}
      padding="auto"
      scale={{
        grade: {
          range: [0, 1],
          ticks: options.map((option) => option.value),
        },
      }}
    >
      <LineAdvance
        position="date*grade"
        color="gradeType"
        shape="smooth"
        area
        tooltip={[
          "gradeType*grade",
          (name: "collaborator" | "referents", value) => {
            return {
              value:
                value !== null
                  ? getClosestGrade(options, value).label
                  : t("reviews.noReferentGrading"),
              name: t(`reviews.${name}` as const) as string,
            };
          },
        ]}
        point
      />

      <Axis
        name="date"
        label={{
          formatter,
        }}
      />
      <Axis
        name="grade"
        position="left"
        label={{
          formatter: (value) =>
            getClosestGrade(options, parseFloat(value)).label,
        }}
      />
      <Legend
        name="user"
        position="bottom"
        itemName={{
          formatter: (text: "collaborator" | "referents") =>
            t(`reviews.${text}` as const),
        }}
      />
      <Slider
        padding={[20, 0, 0, 0]}
        formatter={(value: string) => formatter(value)}
      />
      <Tooltip
        shared
        showMarkers
        showCrosshairs
        title={(
          _,
          datum: {
            date: Date;
          }
        ) => formatter(datum.date)}
      />
    </Chart>
  );
};

export default React.memo(UserGradeOverTimeLineChart);
